import { Link } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { ProjectionKind, useTargetProcessesDataTable } from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import ScoreTag from 'modules/performance_analytics/components/ScoreTag'
import { AlignmentScoreTag } from 'modules/performance_process/components/overview/ProcessTargetDataTable/helpers/AlignmentScoreTag'
import { StatusTag } from 'modules/performance_process/components/reviews/ReviewsDataTable/helpers/StatusTag'
import { InlineEmployee } from 'modules/performance_v2/components/InlineEmployee'

const TargetProcessesOverviewTable = ({ accessId }: { accessId: number }) => {
  const { navigation } = useNavigation()

  const source = useTargetProcessesDataTable(
    [
      {
        identifier: 'name',
        title: i18n.t('employees.profile.performance.reviews.header.name'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ performanceReviewProcess }}',
        colSpan: 1,
        render: ({ performanceReviewProcess_name }) => (
          <Box width="s200">
            <Text.Normal>{performanceReviewProcess_name}</Text.Normal>
          </Box>
        ),
      },
      {
        identifier: 'deadline',
        title: i18n.t('performance_process.reviews.datatable.deadline'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ performanceReviewProcess.endsAt }}',
        colSpan: 1,
        render: ({ performanceReviewProcess_endsAt }) => (
          <Box width="s96">
            <Text.Normal>
              {performanceReviewProcess_endsAt
                ? dateBackendDateTimeToUI(performanceReviewProcess_endsAt)
                : '-'}
            </Text.Normal>
          </Box>
        ),
      },
      {
        identifier: 'status',
        title: i18n.t('performance_process.reviews.datatable.status'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ performanceReviewProcess.status }}',
        colSpan: 1,
        render: ({ performanceReviewProcess_status }) => (
          <Box width="s96">
            {performanceReviewProcess_status && (
              <StatusTag status={performanceReviewProcess_status} />
            )}
          </Box>
        ),
      },
      {
        identifier: 'manager_reviewer',
        title: i18n.t('performance_process.reviews.common.reviewer'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ manager_reviewer }}',
        colSpan: 1,
        render: ({ evaluations }) => {
          const reviewerEmployee = evaluations[0]?.reviewerAccess?.employeeName

          return (
            <Box width="s200">
              {reviewerEmployee ? <InlineEmployee employee={reviewerEmployee} /> : '-'}
            </Box>
          )
        },
      },
      {
        identifier: 'final_score',
        title: i18n.t('performance_process.reviews.target_datatable.final_score.title'),
        fixed: true,
        kind: ProjectionKind.Custom,
        colSpan: 1,
        template: '{{ performanceReviewProcess.score }}',
        render: ({ reviewFinalEmployeeScore }) => (
          <Box width="s140">
            {reviewFinalEmployeeScore ? (
              <ScoreTag
                score={reviewFinalEmployeeScore.score}
                maxScore={reviewFinalEmployeeScore.scaleMax}
              />
            ) : (
              '-'
            )}
          </Box>
        ),
      },
      {
        identifier: 'alignment',
        title: i18n.t('performance_process.reviews.target_datatable.alignment.title'),
        tooltip: i18n.t('employees.profile.performance.reviews.header.tooltip.alignement'),
        fixed: true,
        kind: ProjectionKind.Custom,
        colSpan: 1,
        template: '{{ insightScore.alignmentScore }}',
        render: ({ insightScore_alignmentScore }) => (
          <Box width="s140">
            {insightScore_alignmentScore ? (
              <AlignmentScoreTag alignmentScore={insightScore_alignmentScore} />
            ) : (
              '-'
            )}
          </Box>
        ),
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        colSpan: 1,
        fixed: true,
        sticky: true,
        render: ({ performanceReviewProcess_id }) => (
          <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
            {performanceReviewProcess_id && (
              <Link
                to={navigation.performanceProcess.reviews.review.target.fullPath({
                  reviewId: performanceReviewProcess_id,
                  targetId: performanceReviewProcess_id + '-' + accessId,
                })}
              >
                <Button type="secondary" aria-label="Access review" iconName="arrow-right" />
              </Link>
            )}
          </Box>
        ),
      },
    ],
    {
      variables: {
        accessIds: [accessId],
      },
    }
  )

  return (
    <DataTable.Provider
      source={source}
      pagination={{
        kind: 'offset',
        itemsPerPage: 20,
      }}
      sort={{
        path: 'performanceReviewProcessStartsAt',
        direction: 'desc',
      }}
    >
      <Box gap="s16">
        <DataTable.HeadBar disableDefaultActions />
        <DataTable.Table autoWidth />
      </Box>
    </DataTable.Provider>
  )
}

export default TargetProcessesOverviewTable
